import type { BatchPageRow, TeSpec } from "@/types";
import type { TestResultSearch } from "./testResultSearch";

/** The model a TE ran — its own, or the deployment's when it left it alone. */
export function effectiveModel(spec: TeSpec, defaultModel: string | null): string {
  return spec.model ?? defaultModel ?? "";
}

/** A git ref, or "deployment" when the TE ran what was deployed. */
export function effectiveCode(spec: TeSpec): string {
  return spec.code?.git_ref ?? "deployment";
}

function teFilterActive(search: TestResultSearch): boolean {
  return search.model !== "" || search.code !== "";
}

/** One TE against the model/code filters — both must hold for the same TE. */
export function specMatches(
  spec: TeSpec,
  search: TestResultSearch,
  defaultModel: string | null,
): boolean {
  if (search.model && effectiveModel(spec, defaultModel) !== search.model) return false;
  if (search.code && effectiveCode(spec) !== search.code) return false;
  return true;
}

/**
 * Whether a page can be picked under the filters (artifact `pass`):
 * some TE it ran matches model and code together, and its last run is
 * inside the period. A page that never ran matches only when no TE filter
 * is set — it has nothing to match with.
 */
export function pageMatches(
  row: BatchPageRow,
  search: TestResultSearch,
  defaultModel: string | null,
  now: Date,
): boolean {
  if (teFilterActive(search)) {
    if (!row.te_specs.some((spec) => specMatches(spec ?? {}, search, defaultModel))) return false;
  }
  if (search.days) {
    if (!row.last_run) return false;
    const since = now.getTime() - Number(search.days) * 24 * 60 * 60 * 1000;
    if (new Date(row.last_run.created_at).getTime() < since) return false;
  }
  return true;
}

/** Whether a column should stay at full strength under the TE filters. */
export function columnMatches(
  spec: TeSpec,
  isBaseline: boolean,
  search: TestResultSearch,
  defaultModel: string | null,
): boolean {
  // The baseline is what every other column is read against — it never dims.
  if (isBaseline || !teFilterActive(search)) return true;
  return specMatches(spec, search, defaultModel);
}

/** Every git ref any page ran, for the code filter. */
export function codeOptions(rows: readonly BatchPageRow[]): string[] {
  const refs = new Set<string>();
  for (const row of rows) {
    for (const spec of row.te_specs) {
      const ref = spec?.code?.git_ref;
      if (ref) refs.add(ref);
    }
  }
  return [...refs].sort();
}
